import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ChevronRight, Lock } from "lucide-react";
import { useAccess } from "@/hooks/useAccess";

export default function Subjects() {
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const { can } = useAccess();
  useEffect(() => {
    api.get("/subjects").then((r) => setSubjects(r.data)).finally(() => setLoading(false));
  }, []);

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
      <div className="mb-8">
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[hsl(var(--brand-orange))]">Subjects</div>
        <h1 className="mt-1 font-display text-3xl font-bold">DGCA syllabus — tap in</h1>
        <p className="mt-1 text-sm text-muted-foreground">Each subject has its own topic tree, question bank and quiz modes.</p>
      </div>
      {loading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-44" />)}
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {subjects.map((s) => (
            <Card key={s.id} className="border-border/70">
              <CardContent className="p-6">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-display text-lg font-bold">{s.name}</div>
                    <div className="mt-1 text-xs font-mono uppercase text-[hsl(var(--brand-orange))]">{s.code}</div>
                  </div>
                  <div className="text-right text-xs text-muted-foreground">
                    <div>{s.question_count} Qs</div>
                    <div>{s.topic_count} topics</div>
                  </div>
                </div>
                <p className="mt-3 text-sm text-muted-foreground">{s.description}</p>
                <div className="mt-5 flex flex-wrap gap-2">
                  {!can(s.id) ? (
                    <Button size="sm" asChild className="bg-[hsl(var(--brand-orange))] hover:bg-[hsl(var(--brand-orange))]/90" data-testid={`sub-unlock-${s.code}`}>
                      <Link to="/courses"><Lock className="mr-1.5 h-3.5 w-3.5" /> Unlock with a course</Link>
                    </Button>
                  ) : (<>
                  <Button size="sm" asChild variant="outline" data-testid={`sub-practice-${s.code}`}>
                    <Link to={`/quiz/new?subject=${s.id}&mode=practice`}>Practice</Link>
                  </Button>
                  <Button size="sm" asChild className="bg-[hsl(var(--brand-navy))] hover:bg-[hsl(var(--brand-navy))]/90" data-testid={`sub-mock-${s.code}`}>
                    <Link to={`/quiz/new?subject=${s.id}&mode=mock`}>Mock exam</Link>
                  </Button>
                  <Button size="sm" asChild variant="ghost" data-testid={`sub-view-${s.code}`}>
                    <Link to={`/quiz/new?subject=${s.id}`}>Details <ChevronRight className="ml-1 h-3.5 w-3.5" /></Link>
                  </Button>
                  </>)}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
